<template>
  <div>
    <a-skeleton :paragraph="{ rows: 4 }" v-if="loading" />
    <div class="card" v-else>
      <div class="card-header pt-3 pb-3">
        <div class="d-flex align-items-center justify-content-between">
          <div
            class="d-flex align-items-center"
            :class="{ 'order-1 flex-row-reverse': $i18n.locale === 'ar-AR' }"
          >
            <a-badge
              :count="totalItems"
              :class="$i18n.locale === 'ar-AR' ? 'ml-2' : 'mr-2'"
              :number-style="{
                backgroundColor: 'transparent',
                color: '#999',
                boxShadow: '0 0 0 1px #d9d9d9 inset',
              }"
              :overflowCount="999"
            />
            <h5 class="mb-0">{{ $t("topBar.notification.notifications") }}</h5>
          </div>
          <div
            class="d-flex align-items-center"
            :class="{ 'order-0 flex-row-reverse': $i18n.locale === 'ar-AR' }"
          >
            <div
              :style="{
                width: '350px',
                [$i18n.locale === 'ar-AR' ? 'margin-left' : 'margin-right']:
                  '10px',
              }"
            >
              <a-input-search
                v-model="searchQuery"
                :placeholder="$t('topBar.notification.searchPlaceholder')"
                @search="handleSearch"
                @pressEnter="handleSearch"
              />
            </div>
            <a-button @click="toggleSort">
              <a-icon
                :type="sortAscending ? 'sort-ascending' : 'sort-descending'"
              />
            </a-button>
          </div>
        </div>
      </div>
      <div class="p-5">
        <template v-if="paginatedNotifications.length > 0">
          <div
            class="card"
            v-for="(item, index) in paginatedNotifications"
            :key="index"
            @click="handleNotificationClick(item)"
            :class="{ 'cursor-pointer': getNotificationLink(item) !== '/' }"
          >
            <div class="card-content p-4">
              <div
                class="d-flex align-items-center mb-3 justify-content-between"
              >
                <div class="d-flex">
                  <span
                    v-if="item.schoolarYear"
                    class="badge badge-primary"
                    style="font-size: 11px; padding: 4px 8px"
                  >
                    {{ formatSchoolYear(item.schoolarYear) }}
                  </span>
                </div>
                <div class="d-flex align-items-center">
                  <div class="d-flex flex-column text-right">
                    <div
                      class="d-flex align-items-center justify-content-end mb-1"
                    >
                      <h5 class="mb-0">{{ item.topic }}</h5>
                    </div>
                    <span class="text-muted" style="font-size: 13px">
                      {{ moment(item.createdAt).format("HH:mm DD/MM/YYYY") }}
                    </span>
                  </div>
                  <a-avatar
                    size="small"
                    icon="bell"
                    class="ml-3"
                    style="background-color: #969696"
                  />
                </div>
              </div>
              <p class="mb-0 text-right" dir="rtl">{{ item.message }}</p>
            </div>
          </div>
          <div class="text-center mt-4">
            <a-pagination
              v-model="currentPage"
              :total="total"
              :pageSize="pageSize"
              @change="handlePageChange"
            />
          </div>
        </template>
        <template v-else>
          <div class="text-center p-4">
            <a-empty :description="$t('topBar.notification.noNotifications')" />
          </div>
        </template>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import apiClient from "@/services/axios";
import moment from "moment";

export default {
  data() {
    return {
      loading: false,
      searchQuery: "",
      sortAscending: false,
      notifications: [],
      currentPage: 1,
      pageSize: 10,
      totalItems: 0,
    };
  },

  computed: {
    paginatedNotifications() {
      return this.notifications;
    },
    total() {
      return this.totalItems;
    },
  },

  created() {
    this.fetchNotifications();
    this.markAllAsRead();
  },

  methods: {
    moment,
    formatSchoolYear(schoolarYear) {
      if (!schoolarYear || !schoolarYear.start) return "";
      const startYear = moment(schoolarYear.start).year();
      const endYear = moment(schoolarYear.end).year();
      return `${startYear}/${endYear}`;
    },
    async fetchNotifications() {
      this.loading = true;
      try {
        const response = await apiClient.get("/notifications", {
          params: {
            page: this.currentPage,
            limit: this.pageSize,
            search: this.searchQuery,
            sortOrder: this.sortAscending ? "asc" : "desc",
          },
        });
        this.notifications = response.data.notifications.map((item) => ({
          ...item,
          link: this.getNotificationLink(item),
        }));
        this.totalItems = response.data.pagination.totalItems;
      } catch (error) {
        this.$message.error(this.$t("topBar.notification.errorFetch"));
      }
      this.loading = false;
    },
    getNotificationLink(notif) {
      switch (notif.refType) {
        case "studentNameChange":
          return "/eleve/" + notif.ref;
        case "notifyManagerNewComment":
          return "/actuality";
        case "notifyManagerNewLike":
          return "/actuality";
        case "newTeacherNews":
          return "/actuality/" + notif.ref;
        case "newsValidationResultToAdmin":
          return "/actuality/" + notif.ref;
        case "newStudentPunishment":
          return "/listePunitions";
        case "pickup":
          return "/pickup";
        case "notifyManagerActivityClubJoin":
          return "/activity-club/" + notif.ref;
        case "notifyManagerActivityClubComment":
          return "/activity-club/" + notif.ref;
        case "notifyManagerActivityClubLike":
          return "/activity-club/" + notif.ref;
        default:
          return "/";
      }
    },
    handleNotificationClick(notification) {
      if (notification.link !== "/" && this.$route.path !== notification.link) {
        this.$router.push(notification.link);
      }
    },
    async markAllAsRead() {
      try {
        await apiClient.post("/notifications/mark-as-read", {});
        // Emit an event to update the notification count in the topbar
        this.$root.$emit("notifications-read");
      } catch (error) {
        console.error("Failed to mark notifications as read:", error);
      }
    },
    handleSearch() {
      this.currentPage = 1;
      this.fetchNotifications();
    },
    handlePageChange(page) {
      this.currentPage = page;
      this.fetchNotifications();
    },
    toggleSort() {
      this.sortAscending = !this.sortAscending;
      this.currentPage = 1;
      this.fetchNotifications();
    },
  },
};
</script>

<style scoped>
.text-right {
  text-align: right;
}

[dir="rtl"] {
  direction: rtl;
  unicode-bidi: embed;
}

.cursor-pointer {
  cursor: pointer;
}
</style>
